import React, { memo, useState, useEffect } from "react";
import ItemCard from "./ItemCard";
import './ItemGrid.css';
import ItemListTable from "./ItemListTable";
import { itemListColumns } from "./ItemListColumns";

function ItemGrid({
  filteredCards = [],
  multiSelectMode = false,
  selectedCards = [],
  setSelectedCards = (..._args) => {},
  handleCardClick = (..._args) => {},
  handleTouchStart = (..._args) => {},
  handleTouchEnd = (..._args) => {},
  viewMode = "grid",
  itemsPerPage = 20,
  setItemsPerPage = (..._args) => {},
  itemsPerPageOptions = [10, 20, 50],
  preferenceKey = "hobbli:item-list-preferences:guest",
}) {
  const [currentPage, setCurrentPage] = useState(1);
  

  // Calculate which cards to display
  const totalPages = Math.max(1, Math.ceil(filteredCards.length / itemsPerPage));
  const startIdx = (currentPage - 1) * itemsPerPage;
  const endIdx = startIdx + itemsPerPage;
  const cardsToDisplay = filteredCards.slice(startIdx, endIdx);

  // Reset to page 1 only when the page-size changes.
  // A normal collection refresh (for example after editing a card) replaces
  // filteredCards with a new array, but should not move the user away from
  // the page they were viewing.
  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);

  // If filtering/deleting reduces the number of pages, keep the current page
  // valid without unnecessarily resetting it to the beginning.
  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

      return (
      <div className="item-grid-container">
      {cardsToDisplay.length === 0 ? (
        <div className="empty-state">
          {/* Optional illustration */}
          <div className="empty-state-graphic">
            <img
              src="/Logos/EmptyCollection.png"
              alt="Empty collection illustration"
            />
          </div>

          <h2>Welcome to Hobbli 👋</h2>

          <p className="empty-state-intro">
            Looks like your collection is empty for now — no worries, everyone
            starts somewhere.
          </p>

          <div className="empty-state-actions">
            <div className="empty-state-card">
              <h4>Add items one at a time</h4>
              <p>
                Click <strong>Add Item</strong> above to enter details, snap photos,
                and build your collection piece by piece.
              </p>
            </div>

            <div className="empty-state-card">
              <h4>Adding a lot at once?</h4>
              <p>
                Open the menu ☰ in the top-left, head to{" "}
                <strong>Account Settings</strong>, and use{" "}
                <strong>Add Items via Excel</strong> to upload a bunch in one shot.
              </p>
            </div>
          </div>

          <p className="empty-state-footer">
            Not here to track a collection? Okay… weird 😄 — but you do you.
            <br />
            Check out the Shop, Social, Insights, or Games from the menu.
          </p>

          <p className="empty-state-signoff">
            However you Hobbli, have fun with it.
          </p>
        </div>
      ) : viewMode === "grid" ? (
        <div className="item-grid grid">
          {cardsToDisplay.map((card, index) => {
            const absoluteIndex = startIdx + index;
            const cardKey = card.id ?? absoluteIndex;
            const isSelected = selectedCards.some((id) => String(id) === String(cardKey));


              return (
                <ItemCard
                  key={cardKey}
                  card={card}
                  index={absoluteIndex}
                  isSelected={isSelected}
                  multiSelectMode={multiSelectMode}
                  handleCardClick={handleCardClick}
                  handleTouchStart={handleTouchStart}
                  handleTouchEnd={handleTouchEnd}
                />
              );
            })}
          </div>
        ) : (
          <ItemListTable
            items={cardsToDisplay}
            columns={itemListColumns}
            selectedItems={selectedCards}      // like isSelected in grid
            setSelectedItems={setSelectedCards} // needed to toggle selection
            multiSelectMode={multiSelectMode}
            onRowClick={(index, item, event) =>
              handleCardClick(startIdx + index, item, event)
            }
            preferenceKey={preferenceKey}
          />
        )}

        {/* Pagination Controls */}
        <div className="item-grid-controls">
          <div className="pagination-summary">
            Showing {startIdx + 1}–{Math.min(endIdx, filteredCards.length)} of {filteredCards.length}
          </div>
          <div className="items-per-page">
            <label>
              Items per page:
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
              >
                {itemsPerPageOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="pagination-buttons">
            <button
              className="pagination-button"
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span>
              Page {currentPage} / {totalPages}
            </span>
            <button
              className="pagination-button"
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    );
}

export default memo(ItemGrid);
